// Promote a completed test run to the client's storage, through the worker: each file is
// recorded once in DataIO, byte-identical files are skipped, a file that landed live after the
// test run is refused, and the job's transform runs once. Never counts toward probation, a
// completion or the day's run. A reason is required.
//
// On open it reads GET .../promotions/{run_id}: the test run's files (the table) and the worker's
// result, if it was promoted before. After Promote it follows that result (status running ->
// promoted | nothing_new | refused | failed) and shows each file's action.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { getPromotion, notThere, promote } from './api';
import { fmtClock, jobName } from './deskModel';
import { MONO, useTones } from './tones';

const POLL_MS = 5000;
const POLL_TRIES = 24;
const DONE = ['promoted', 'nothing_new', 'refused', 'failed'];
const ACTION_TEXT = {
    write: 'to write', written: 'Written', identical: 'Identical, skipped', newer_live: 'Newer live file: not replaced', failed: 'Failed',
};

const fmtSize = (n) => (typeof n !== 'number' ? '—' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);

// The table: the test run's files, each with the worker's action once it has a result. The
// worker's paths may carry a prefix, so a file matches the result entry whose path ends with it.
export const promoteRows = (resp) => {
    const result = resp?.promotion?.files || [];
    const actionOf = (name) => result.find((f) => f.path === name || String(f.path || '').endsWith(`/${name}`)) || null;
    return (resp?.files || []).map((f) => {
        const r = actionOf(f.name);
        return { name: f.name, size: f.size, action: r ? (ACTION_TEXT[r.action] || r.action) : null, error: r?.error || null };
    });
};

// After a click, a result only counts if the worker took the request at or after the click
// (60 s of clock skew allowed): an older one is the previous attempt's, still in the blob.
export const SKEW_MS = 60 * 1000;
export const isFreshResult = (p, clickedAtMs) => {
    if (!p) return false;
    const at = Date.parse(p.requested_at || p.started_at || '');
    return !Number.isNaN(at) && at >= clickedAtMs - SKEW_MS;
};

export const promotionText = (p) => {
    if (!p) return null;
    const lead = { running: 'Promoting…', promoted: 'Promoted', nothing_new: 'Nothing new: every file is already in the client\'s storage',
        refused: 'Refused', failed: 'Failed' }[p.status] || p.status;
    return [lead, p.refused, p.error, p.note].filter(Boolean).join(': ');
};

// target: { row, run } or null.
const PromoteDialog = ({ target, onClose, onDone }) => {
    const t = useTones();
    const [resp, setResp] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [queued, setQueued] = useState(false);
    const poll = useRef(null);
    const row = target?.row;
    const run = target?.run;

    const read = useCallback(() => getPromotion(row.id, run.run_id), [row, run]);
    useEffect(() => {
        setReason(''); setError(null); setQueued(false); setResp(null); setLoadError(null);
        if (!target) return undefined;
        read().then(setResp).catch((e) => setLoadError(notThere(e) ? 'Promote needs the newer Browser Desk API, which is not deployed yet.' : e.message));
        return () => clearTimeout(poll.current);
    }, [target, read]);

    if (!target) return <Dialog open={false} />;
    const rows = promoteRows(resp);
    const result = resp?.promotion || null;
    const finished = result && DONE.includes(result.status);
    // a refused or failed earlier attempt can be tried again (e.g. after a fix); anything else is done
    const canTry = !queued && (!result || ['refused', 'failed'].includes(result.status));
    const client = [...new Set((row.jobs || []).map((j) => j.client).filter(Boolean))].join(', ') || 'the client';
    const jobs = (run.job_types || []).map((jt) => jobName(row, jt));

    // Polls until this click's result is done or the window ends; an older result is hidden.
    const follow = (clickedAt, n = 0) => {
        const again = () => { if (n < POLL_TRIES) poll.current = setTimeout(() => follow(clickedAt, n + 1), POLL_MS); };
        read().then((r) => {
            const fresh = isFreshResult(r?.promotion, clickedAt);
            setResp({ ...r, promotion: fresh ? r.promotion : null });
            if (fresh && DONE.includes(r.promotion.status)) return;
            again();
        }).catch(again);
    };
    const submit = async () => {
        setBusy(true); setError(null);
        const clickedAt = Date.now();
        try {
            await promote(row.id, run.run_id, reason.trim());
            setQueued(true);
            setResp((r) => ({ ...r, promotion: null })); // an earlier attempt's result is not this one's
            onDone('Promotion queued: the worker copies the files and runs the transform once');
            poll.current = setTimeout(() => follow(clickedAt), POLL_MS);
        } catch (e) {
            setError(notThere(e) ? 'Promote needs the newer Browser Desk API, which is not deployed yet.' : e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle className="d-font">
                Promote test run {String(run.run_id).slice(0, 8)} to {client}&apos;s storage?
                <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                    {jobs.join(', ') || 'all jobs'}{run.at ? ` · ${fmtClock(run.at, row.timezone)}` : ''}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loadError && <Alert severity="info" sx={{ mb: 1.5 }}>{loadError}</Alert>}
                {!resp && !loadError && <CircularProgress size={22} sx={{ mb: 1.5 }} />}
                {resp && !rows.length && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>No files in this test run&apos;s storage: nothing to promote.</Alert>
                )}
                {rows.length > 0 && (
                    <Box sx={{ overflowX: 'auto', mb: 1.5, maxHeight: 320 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['File', 'Size', 'Action'].map((h) => (
                                        <TableCell key={h} className="d-font" sx={{ fontSize: 12, fontWeight: 700 }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.name}>
                                        <TableCell sx={{ fontFamily: MONO, fontSize: 12, wordBreak: 'break-all' }}>{r.name}</TableCell>
                                        <TableCell className="d-font" sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtSize(r.size)}</TableCell>
                                        <TableCell className="d-font" sx={{ fontSize: 12, color: r.error ? t.bad : r.action ? 'text.primary' : t.muted }}>
                                            {r.action ? `${r.action}${r.error ? `: ${r.error}` : ''}` : 'decided by the worker'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                {!result && (
                    <Box className="d-font" sx={{ fontSize: 13, p: 1.25, mb: 1.5, borderRadius: 1, bgcolor: t.dark ? 'action.hover' : '#f5f6f8' }}>
                        <b>This will:</b>
                        <Box>· copy the test run&apos;s files through the worker, recorded in DataIO as desk:{row.id} (promoted from test); identical files are skipped</Box>
                        <Box>· refuse a file that landed live after this test run</Box>
                        <Box>· run the {jobs.join(', ') || 'job\'s'} transform once</Box>
                        <Box>· not count toward probation, a completion or the day&apos;s run</Box>
                    </Box>
                )}
                {canTry && (
                    <TextField label="Why (required)" fullWidth size="small" multiline minRows={2} value={reason} disabled={queued}
                        onChange={(e) => setReason(e.target.value)} placeholder="e.g. HMH fix verified on test; Monday's live run missed these grades" />
                )}
                {queued && !result && <Alert severity="info" sx={{ mt: 1.5 }}>Queued. Waiting for the worker&apos;s result…</Alert>}
                {result && (
                    <Alert severity={['refused', 'failed'].includes(result.status) ? 'error' : result.status === 'running' ? 'info' : 'success'} sx={{ mt: 1.5 }}>
                        {promotionText(result)}
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none' } }}>
                <Button onClick={onClose}>{queued || finished ? 'Close' : 'Cancel'}</Button>
                {canTry && (
                    <Button variant="contained" disabled={busy || !reason.trim() || Boolean(loadError) || !rows.length} onClick={submit}>
                        {`Promote ${rows.length} file${rows.length === 1 ? '' : 's'}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PromoteDialog;
